import React, {useCallback, useEffect, useRef} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../../constants";
import METADATA from "../../../assets/data/search.meta.json";
import {ISearchState} from "../../../types/redux/search";
import {isElement} from "react-dom/test-utils";

/* Redux*/
import {useSelector} from "react-redux";
import {RootState} from "../../../redux";
import ExpandField from "../components/expand-field";

export interface ICardFieldContent {
  elem: JSX.Element,
  selectionCount: number
}

export interface IUseCardField {
  getFieldContent (id: string, value: any, label: string, link?: string | null): ICardFieldContent
}

interface ISelection {
  start: number,
  end: number
}

const useCardField = (): IUseCardField => {
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search)
  const searchTerms:React.MutableRefObject<string[]> = useRef([]);
  const allFields: any = METADATA.allFields;
  const allFields_reverse: any = METADATA.allFields_reverse;

  const filterSearchTerms = useCallback((): string[] =>{
    /* Collect all the terms to select */
    /* @ts-ignore */
    const terms: string[] = searchState.query.filter(q => q.term !== null).map(q=> q.term).join(' ').split(' ');
    const filteredTerms: string[] = [];
    const QUOTE: string = '"';

    for (let i=0; i<terms.length; i++) {
      if (!terms[i]) continue;

      const term: string = terms[i];
      if (term[0] === QUOTE) {
        /* found start of a quote, need to find end if one exists */
        let found: boolean = false;
        let j: number = i + 1;
        let multiWord: string = term.substring(1, term.length);
        while (!found && j < terms.length) {
          const next: string = terms[j];
          if (next[next.length - 1] === QUOTE) {
            multiWord += (' ' + next.substring(0, next.length - 1))
            found = true
          } else {
            multiWord += (' ' + next)
          }
          j++;
        }

        if (found) {
          /* matching quote found */
          filteredTerms.push(multiWord);
          i = j - 1;
        } else {
          /* no matching quote found */
          filteredTerms.push(term)
        }
      } else {
        /* no quote */
        filteredTerms.push(term)
      }
    }
    return filteredTerms
  }, [searchState.query])

  useEffect(()=>{
    /* normal case, performed search*/
    searchTerms.current = filterSearchTerms()
  }, [searchState.query, filterSearchTerms])

  const getFieldContent = (id: string, value: any, label: string, link: string | null): ICardFieldContent => {
    if (!value) return {elem: <></>, selectionCount: 0};

    if (isElement(value)) return {elem: value, selectionCount:0};
    if (searchTerms.current.length === 0) {
      /* special case - reload page or shared link */
      searchTerms.current = filterSearchTerms()
    }

    if (Array.isArray(value)){
      value = value.join(', ')
    }
    value = String(value);

    /* Find all selections */
    let text: string = value;
    const selections: ISelection[] = [];

    searchTerms.current.forEach(t=>{
      // handle field search t = "cited:*"
      let _continue: boolean = false;
      const tSplit: string[] = t.split(':');
      if ((tSplit.length > 0) && ((allFields[tSplit[0]]) || allFields_reverse[tSplit[0]])) {
        if (id.toLowerCase() === tSplit[0].toLowerCase()) {
          t = tSplit[1];
        } else {
          /* field search and term is not the field then skip highlighting */
          _continue = true;
        }
      }

      if (!_continue) {
        if (t === Constants.ASTERISK) {
          /* special case: field search entire text */
          selections.push({
            start: 0,
            end: value.length
          })
        } else {
          let results:any[] = [];
          try {
            results = value.matchAll(new RegExp(t, 'gi'));
          } catch (e) {
            //console.log('matchAll ERROR: ' + e)
          }

          for (let result of results) {
            selections.push({
              start: result.index,
              end: result.index + result[0].length
            })
          }
          selections.sort((a, b) => (a.start > b.start) ? 1 : -1)
        }

        /* Insert selections */
        if (selections.length > 0) {
          text = value.substring(0, selections[0].start);
          for (let i=0; i<selections.length; i++) {
            const select: any = selections[i];
            text += ('<sel>' + value.substring(select.start, select.end) + '</sel>');
            if (i<(selections.length - 1)) {
              text += value.substring(select.end, selections[i+1].start)
            } else {
              text += value.substring(select.end, value.length)
            }
          }
        }
      }
    });

    return {
      elem: <ExpandField
        defaultExpanded={selections.length > 0}
        label={label}
        text={text}
        link={link}/>,
      selectionCount: selections.length
    }
  }

  return {
    getFieldContent
  }
};

export default useCardField;
